import { ChangeDetectionStrategy, Component, Inject, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';

import { ButtonComponent, IconComponent } from '@mode/capra';
import { FileUploadComponent } from './file-upload.component';
import { FileUploadEvent } from '@mode/shared/contract-common';
import { FileUploadDialogData, FileUploadDialogResult } from '@mode/shared/contract-common';
import { DialogHeaderComponent } from '../dialog/header/dialog-header.component';

/**
 * A reusable dialog component for file uploads.
 *
 * This component provides a standardized file upload experience that follows
 * the application's dialog patterns. It can be configured for different file
 * types and use cases through the FileUploadDialogData interface.
 *
 * @example
 * ```typescript
 * // Using the FileUploadDialogService
 * this.fileUploadDialogService.open({
 *   title: 'Upload Documents',
 *   config: DOCUMENT_UPLOAD_CONFIG,
 *   uploadButtonText: 'Upload Files'
 * }).subscribe(result => {
 *   if (result?.success) {
 *     console.log('Uploaded files:', result.files);
 *   }
 * });
 * ```
 */
@Component({
  standalone: true,
  selector: 'mode-file-upload-dialog',
  templateUrl: './file-upload-dialog.component.html',
  styleUrls: ['./file-upload-dialog.component.scss'],
  imports: [CommonModule, ButtonComponent, FileUploadComponent, DialogHeaderComponent, IconComponent],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class FileUploadDialogComponent implements OnInit {
  uploading = false;
  @Output() uploadClicked = new EventEmitter<File[]>();
  public selectedFiles: File[] = [];
  public error: string | null = null;
  public hasValidationErrors = false;
  public errorStatusCode: number | null = null;

  showError(message: string, statusCode?: number) {
    this.error = message;
    this.errorStatusCode = statusCode || null;
    this.uploading = false;
  }

  constructor(
    public dialogRef: MatLegacyDialogRef<FileUploadDialogComponent, FileUploadDialogResult>,
    @Inject(MAT_LEGACY_DIALOG_DATA) public data: FileUploadDialogData
  ) {}

  ngOnInit(): void {
    this.selectedFiles = [];
  }

  public get hasFiles(): boolean {
    return this.selectedFiles.length > 0;
  }

  public get is4xxError(): boolean {
    return this.errorStatusCode !== null && this.errorStatusCode >= 400 && this.errorStatusCode < 500;
  }

  public onFilesSelected(event: any): void {
    if (event.files && event.files.length > 0) {
      this.error = null;
      this.hasValidationErrors = false;
      this.errorStatusCode = null;
    }
    this.selectedFiles = event.files ? event.files.map((uf: any) => uf.file) : event;
  }

  public onFileRemoved(event: FileUploadEvent): void {
    this.error = null;
    this.hasValidationErrors = false;
    this.selectedFiles = event.files.map((uploadedFile) => uploadedFile.file);
  }

  public onUpload(): void {
    this.error = null;
    if (!this.hasFiles) {
      return;
    }
    this.uploading = true;
    this.uploadClicked.emit([...this.selectedFiles]);
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Handles ESC key and backdrop clicks
   */
  public onNoClick(): void {
    this.onCancel();
  }

  startUploading() {
    this.uploading = true;
  }

  stopUploading() {
    this.uploading = false;
  }

  onUploadError(event: any): void {
    this.error = event[0];
    this.hasValidationErrors = event && event.length > 0;
  }
}
